import { Head, Link, router } from '@inertiajs/react'
import { DeviceType } from './types.ts'
import {ArrowLeft, Trash2} from "lucide-react";
import Pagination from "@/components/pagination.tsx";

interface ShowProps {
  device: DeviceType
  payments: Array<{
    id: string
    collected_at: string
    payment_method?: string
    tax_payer_name?: string
    agent_name?: string
    collect_session_code?: string
    amount: number
  }>
  payments_pagination: {
    current_page: number
    total_pages: number
    total_count: number
    per_page: number
    next_page: number | null
    prev_page: number | null
  }
  collect_sessions: Array<{
    id: string
    code?: string
    status?: string
    start_at?: string
    end_at?: string | null
    agent_name?: string
  }>
  collect_sessions_pagination: {
    current_page: number
    total_pages: number
    total_count: number
    per_page: number
    next_page: number | null
    prev_page: number | null
  }
  can_destroy?: boolean
}

export default function Show({
  device,
  payments,
  payments_pagination,
  collect_sessions,
  collect_sessions_pagination,
  can_destroy = false
}: ShowProps) {
  const statusLabel =
    device.status === "Active"
      ? "Actif"
      : device.status === "Assigned"
        ? "Assigné"
        : device.status === "blocked"
          ? "Bloqué"
          : device.status

  const statusClass =
    device.status === "Active"
      ? "bg-success/10 text-success"
      : device.status === "Assigned"
        ? "bg-muted text-muted-foreground"
        : device.status === "blocked"
          ? "bg-warning/10 text-warning"
          : "bg-muted text-muted-foreground"

  const collectionStatusLabel =
    device.last_collection_status === "open"
      ? "Actif"
      : device.last_collection_status === "closed"
        ? "Fermé"
        : device.last_collection_status === "reconciled"
          ? "Rapproché"
          : "-"

  const collectionStatusClass =
    device.last_collection_status === "open"
      ? "bg-success/10 text-success"
      : device.last_collection_status === "reconciled"
        ? "bg-accent/10 text-accent"
      : device.last_collection_status === "closed"
          ? "bg-muted text-muted-foreground"
          : "bg-muted text-muted-foreground"

  const collectSessionStatusLabel = (status?: string) =>
    status === "open" ? "Actif" : status === "closed" ? "Fermé" : status === "reconciled" ? "Rapproché" : "-"

  const collectSessionStatusClass = (status?: string) =>
    status === "open"
      ? "bg-success/10 text-success"
      : status === "reconciled"
        ? "bg-accent/10 text-accent"
        : status === "closed"
          ? "bg-muted text-muted-foreground"
          : "bg-muted text-muted-foreground"

  const handleDestroy = () => {
    if (!window.confirm(`Supprimer le terminal "${device.name}" ?`)) return

    router.delete(`/admin/devices/${device.id}`)
  }

  return (
    <>
      <Head title={`Device #${device.id}`} />

      <div className="p-8">
        <div className="mb-6">
          <Link href="/admin/devices">
            <button className="mb-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4" />
              Retour
            </button>
          </Link>

          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-3xl font-bold text-foreground">{device.name}</h2>
              <p className="text-muted-foreground">{device.code}</p>
            </div>
            {can_destroy && (
              <button
                type="button"
                className="flex items-center gap-2 rounded-lg border border-destructive px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10"
                onClick={handleDestroy}
              >
                <Trash2 className="h-4 w-4" />
                Supprimer
              </button>
            )}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="rounded-lg border border-border bg-card p-6 lg:col-span-2">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Détails du terminal</h3>
              <span className={`rounded-full px-3 py-1 text-xs font-medium ${statusClass}`}>
                {statusLabel}
              </span>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-xs text-muted-foreground">Agent assigné</p>
                <p className="mt-1 text-sm font-semibold text-foreground">{device.assigned_agent_name || "Non assigné"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Communauté</p>
                <p className="mt-1 text-sm font-semibold text-foreground">{device.community_name || "-"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Numéro de série</p>
                <p className="mt-1 text-sm font-semibold text-foreground">{device.serial_number || "-"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">IMEI</p>
                <p className="mt-1 text-sm font-semibold text-foreground">{device.imei || "-"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Dernière synchronisation</p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  {device.last_sync_at ? new Date(device.last_sync_at).toLocaleString() : "-"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Dernière collecte</p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  {device.last_collection_at ? new Date(device.last_collection_at).toLocaleString() : "-"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Statut de collecte</p>
                <span className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${collectionStatusClass}`}>
                  {collectionStatusLabel}
                </span>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Actif</p>
                <p className="mt-1 text-sm font-semibold text-foreground">{device.active ? "Oui" : "Non"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">ID Communauté</p>
                <p className="mt-1 text-sm font-semibold text-foreground">{device.community_name || "-"}</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-card p-6">
              <p className="text-xs text-muted-foreground">Total paiements</p>
              <p className="mt-2 text-2xl font-semibold text-foreground">{device.total_payments_count || 0}</p>
            </div>
            <div className="rounded-lg border border-border bg-card p-6">
              <p className="text-xs text-muted-foreground">Montant du jour</p>
              <p className="mt-2 text-2xl font-semibold text-success">
                {Number(device.today_collected_amount || 0).toFixed(2)} FCFA
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2 mt-6">
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Paiements du terminal</h3>
              <p className="text-sm text-muted-foreground">{payments_pagination.total_count} paiements</p>
            </div>

            <div className="space-y-3">
              {payments.length === 0 && (
                <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
                  Aucun paiement trouvé pour ce terminal.
                </div>
              )}

              {payments.map((payment) => (
                <div key={payment.id} className="rounded-lg border border-border p-4">
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="font-medium text-foreground">{payment.tax_payer_name || "Contribuable inconnu"}</p>
                      <p className="text-sm text-muted-foreground">
                        Agent : {payment.agent_name || "Inconnu"} • Session: {payment.collect_session_code || "-"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {payment.payment_method || "cash"} • {new Date(payment.collected_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-success">{Number(payment.amount || 0).toFixed(2)} FCFA</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4">
              <Pagination
                currentPage={payments_pagination.current_page}
                totalPages={payments_pagination.total_pages}
                basePath={`/admin/devices/${device.id}`}
                pageParam="payments_page"
                query={{ sessions_page: collect_sessions_pagination.current_page }}
              />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Sessions de collecte</h3>
              <p className="text-sm text-muted-foreground">{collect_sessions_pagination.total_count} sessions</p>
            </div>

            <div className="space-y-3">
              {collect_sessions.length === 0 && (
                <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
                  Aucune session trouvée pour ce terminal.
                </div>
              )}

              {collect_sessions.map((session) => (
                <Link
                  key={session.id}
                  href={`/collect_sessions/${session.id}`}
                  className="block rounded-lg border border-border p-4 transition-colors hover:bg-secondary/50"
                >
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="font-medium text-foreground">{session.code || `Session ${session.id}`}</p>
                      <p className="text-sm text-muted-foreground">Agent : {session.agent_name || "Inconnu"}</p>
                      <p className="text-xs text-muted-foreground">
                        Début: {session.start_at ? new Date(session.start_at).toLocaleString() : "-"}
                        {session.end_at ? ` • Fin: ${new Date(session.end_at).toLocaleString()}` : ""}
                      </p>
                    </div>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${collectSessionStatusClass(session.status)}`}>
                      {collectSessionStatusLabel(session.status)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-4">
              <Pagination
                currentPage={collect_sessions_pagination.current_page}
                totalPages={collect_sessions_pagination.total_pages}
                basePath={`/admin/devices/${device.id}`}
                pageParam="sessions_page"
                query={{ payments_page: payments_pagination.current_page }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
